import React, { useState, useEffect } from "react";
import {
  ExclamationCircleIcon,
  CheckIcon,
  EyeIcon,
} from "@heroicons/react/24/outline";
import { Alert } from "../../types";
import { alertDefinitions } from "../../data/alerts";
import { useStore } from "../../store";
import { isAlertFresh } from "../../utils/alertHelpers";
// import { alertWidgetSuggestions } from "../../data/widgets";

interface AlertBannerItemProps {
  alert: Alert;
}

export const AlertBannerItem: React.FC<AlertBannerItemProps> = ({ alert }) => {
  const { acknowledgeAlert, markAlertInteracted } = useStore();
  const [isFresh, setIsFresh] = useState(false);

  // Get alert definition
  const definition =
    alertDefinitions[alert.definitionId as keyof typeof alertDefinitions];
  const alertType = definition?.type || "info";
  const alertTitle = definition?.title || "Unknown Alert";
  const alertMessage = definition?.message || "No message available";

  // Widget suggestion handled globally in AlertBanner

  // Recompute freshness periodically (3-minute window or until interacted)
  useEffect(() => {
    const update = () => setIsFresh(isAlertFresh(alert));
    update();
    const interval = setInterval(update, 1000);
    return () => clearInterval(interval);
  }, [alert]);

  const onViewDetails = (id: string) => {
    console.log("View details:", id);
    try {
      markAlertInteracted(id);
    } catch {}
  };

  const getAlertIcon = (type: string) => {
    switch (type) {
      case "error":
        // return <ExclamationCircleIcon className="w-5 h-5 text-crane-accent" />;
        return (
          <img src="/assets/icon-error.png" className="w-5 h-5" alt="error" />
        );
      case "warning":
        // return <ExclamationTriangleIcon className="w-5 h-5 text-crane-amber" />;
        return (
          <img
            src="/assets/icon-warning.png"
            className="w-5 h-5"
            alt="warning"
          />
        );
      case "info":
        return (
          // <InformationCircleIcon className="w-5 h-5 text-crane-blue-gray" />
          <img src="/assets/icon-info.png" className="w-5 h-5" alt="info" />
        );
      case "critical":
        return <ExclamationCircleIcon className="w-5 h-5 text-crane-red" />;
      default:
        return null;
    }
  };

  const getAlertStyle = (isFreshFlag: boolean, type: string) => {
    if (isFreshFlag) {
      switch (type) {
        case "error":
          return "bg-crane-error border border-crane-accent";
        case "warning":
          return "bg-crane-amber-dark border border-crane-amber";
        case "critical":
          return "bg-crane-dark border border-crane-red";
        default:
          return "bg-crane-dark border border-crane-blue-gray";
      }
    }
    return "bg-crane-dark border border-crane-neutral-border";
  };

  return (
    <div
      className={`${getAlertStyle(
        isFresh,
        alertType
      )}  rounded-lg p-4 w-full min-h-24 alert-banner-item`}
    >
      <div className="flex items-center space-x-4">
        <div className="flex-1">
          <div className="flex items-center space-x-2">
            {getAlertIcon(alertType)}
            <h3 className="crane-text-body-lg font-medium text-crane-white line-clamp-1">
              {alertTitle}
            </h3>
            <span className="text-crane-gray">{alert.timestamp}</span>
          </div>
          <p className="crane-text-body-lg mt-1 line-clamp-1 text-crane-light-gray">
            {alertMessage}
          </p>
        </div>
        <div className="flex space-x-2">
          <button
            onClick={() => acknowledgeAlert(alert.id)}
            className="crane-button flex items-center space-x-2"
          >
            <CheckIcon className="w-4 h-4" />
            <span>Acknowledge</span>
          </button>
          <button
            onClick={() => onViewDetails(alert.id)}
            className="crane-button-outline flex items-center space-x-2"
          >
            <EyeIcon className="w-4 h-4" />
            <span>Details</span>
          </button>
        </div>
      </div>

      {/* Widget suggestion button moved to AlertBanner for global control */}
    </div>
  );
};
